import * as actions from './actions.js';
import { ReduxUtils } from '@dw/pwa-helpers/index.js';
import get from 'lodash-es/get.js';

/**
 * Manages the client side of the time-tracking integration: in-flight request
 * status for each server call, the lazily-fetched provider lookups (tags and
 * customers), the card held while a blocked Start Timer waits on a dialog, and the
 * Done/Trash move prompts closed on this device (`dismissedMoves`, persisted in `store.js`).
 *
 * `TimeTrackerAccountSettings`, `TimeTrackerAccountUserSettings` and `ActiveTimer`
 * are NOT in this reducer - they live in Firestore and are read at selector time.
 */
const INITIAL_STATE = {
  requests: {},
  billable: {},
  lastEntry: {},
  tags: {},
  customers: {},
  pendingStart: null,
  dismissedMoves: {},
};

const REQUEST_OPERATION_BY_ACTION_TYPE = {
  [actions.CONNECT]: 'connect',
  [actions.CONNECT_SUCCESS]: 'connect',
  [actions.CONNECT_FAILED]: 'connect',
  [actions.CONNECT_USER]: 'userConnect',
  [actions.CONNECT_USER_SUCCESS]: 'userConnect',
  [actions.CONNECT_USER_FAILED]: 'userConnect',
  [actions.DISCONNECT]: 'disconnect',
  [actions.DISCONNECT_SUCCESS]: 'disconnect',
  [actions.DISCONNECT_FAILED]: 'disconnect',
  [actions.UPDATE_SETTINGS]: 'settings',
  [actions.UPDATE_SETTINGS_SUCCESS]: 'settings',
  [actions.UPDATE_SETTINGS_FAILED]: 'settings',
  [actions.START]: 'start',
  [actions.START_SUCCESS]: 'start',
  [actions.START_FAILED]: 'start',
  [actions.START_DEFERRED]: 'start',
  [actions.STOP]: 'stop',
  [actions.STOP_SUCCESS]: 'stop',
  [actions.STOP_FAILED]: 'stop',
  [actions.UPDATE]: 'update',
  [actions.UPDATE_SUCCESS]: 'update',
  [actions.UPDATE_FAILED]: 'update',
  [actions.DISCARD]: 'discard',
  [actions.DISCARD_SUCCESS]: 'discard',
  [actions.DISCARD_FAILED]: 'discard',
};

const SUCCESS_TYPES = [
  actions.START_DEFERRED,
  actions.CONNECT_SUCCESS,
  actions.CONNECT_USER_SUCCESS,
  actions.DISCONNECT_SUCCESS,
  actions.UPDATE_SETTINGS_SUCCESS,
  actions.START_SUCCESS,
  actions.STOP_SUCCESS,
  actions.UPDATE_SUCCESS,
  actions.DISCARD_SUCCESS,
];

const FAILED_TYPES = [
  actions.CONNECT_FAILED,
  actions.CONNECT_USER_FAILED,
  actions.DISCONNECT_FAILED,
  actions.UPDATE_SETTINGS_FAILED,
  actions.START_FAILED,
  actions.STOP_FAILED,
  actions.UPDATE_FAILED,
  actions.DISCARD_FAILED,
];

/**
 * The persisted `dismissedMoves` is restored before this reducer first runs, so the slice
 * arrives holding that field alone and the default would never apply.
 */
const withDefaults = (state) => (state && state.requests ? state : { ...INITIAL_STATE, ...state });

export const timeTrackerReducer = (state, action) => {
  state = withDefaults(state);
  const operation = REQUEST_OPERATION_BY_ACTION_TYPE[action.type];
  if (operation) {
    if (SUCCESS_TYPES.includes(action.type)) {
      return ReduxUtils.replace(state, `requests.${operation}`, { status: 'SUCCESS' });
    }

    if (FAILED_TYPES.includes(action.type)) {
      return ReduxUtils.replace(state, `requests.${operation}`, { status: 'FAILED', error: action.error });
    }

    // The dispatch (CONNECT/START/etc.) itself - mark in-flight.
    return ReduxUtils.replace(state, `requests.${operation}`, { status: 'IN_PROGRESS' });
  }

  switch (action.type) {
    // Nothing is carried over while refetching. A stale `editable` is worse than none:
    // it would leave the toggle live for someone the tracker has since stopped allowing
    // to change it, and the answer is only current at the moment it is read.
    case actions.LOAD_BILLABLE_STARTED:
      return ReduxUtils.replace(state, `billable.${action.accountId}`, { status: 'IN_PROGRESS' });

    case actions.LOAD_BILLABLE_SUCCESS:
      return ReduxUtils.replace(state, `billable.${action.accountId}`, {
        status: 'SUCCESS',
        billable: !!(action.status && action.status.billable),
        editable: !!(action.status && action.status.editable),
      });

    case actions.LOAD_BILLABLE_FAILED:
      return ReduxUtils.replace(state, `billable.${action.accountId}`, { status: 'FAILED' });

    // `entry` stays undefined on a 204 - the tracker has nothing closed yet, which is an
    // ordinary answer rather than a failure, and the hint simply has nothing to offer.
    // The cached entry stays while a refresh is in flight, so the hint shows it rather than
    // blanking and coming back.
    case actions.LOAD_LAST_ENTRY_STARTED:
      return ReduxUtils.replace(state, `lastEntry.${action.accountId}`, {
        ...get(state, `lastEntry.${action.accountId}`),
        status: 'IN_PROGRESS',
      });

    case actions.LOAD_LAST_ENTRY_SUCCESS:
      return ReduxUtils.replace(state, `lastEntry.${action.accountId}`, {
        status: 'SUCCESS',
        entry: action.entry,
      });

    case actions.LOAD_LAST_ENTRY_FAILED:
      return ReduxUtils.replace(state, `lastEntry.${action.accountId}`, { status: 'FAILED' });

    // `LOAD_TAGS` itself is not recorded: the saga decides whether this fetch actually
    // happens, and reducers run before sagas see the action - so marking IN_PROGRESS
    // here would be the saga's own skip-check reading what this dispatch just wrote.
    case actions.LOAD_TAGS_STARTED:
      return ReduxUtils.replace(state, `tags.${action.accountId}`, { status: 'IN_PROGRESS', list: [] });

    case actions.LOAD_TAGS_SUCCESS:
      return ReduxUtils.replace(state, `tags.${action.accountId}`, { status: 'SUCCESS', list: action.list || [] });

    case actions.LOAD_TAGS_FAILED:
      return ReduxUtils.replace(state, `tags.${action.accountId}`, { status: 'FAILED', list: [] });

    // See `LOAD_TAGS_STARTED` above. The previous list is kept while refetching so a
    // forced refresh does not blank the field the admin is looking at.
    case actions.LOAD_CUSTOMERS_STARTED:
      return ReduxUtils.replace(state, `customers.${action.accountId}`, { status: 'IN_PROGRESS', list: state.customers[action.accountId]?.list || [] });

    case actions.LOAD_CUSTOMERS_SUCCESS:
      return ReduxUtils.replace(state, `customers.${action.accountId}`, { status: 'SUCCESS', list: action.list || [] });

    case actions.LOAD_CUSTOMERS_FAILED:
      return ReduxUtils.replace(state, `customers.${action.accountId}`, { status: 'FAILED', list: [] });

    case actions.SET_PENDING_START:
      return ReduxUtils.replace(state, 'pendingStart', { cardId: action.cardId, boardId: action.boardId });

    case actions.CLEAR_PENDING_START:
      return ReduxUtils.replace(state, 'pendingStart', null);

    case actions.SET_DISMISSED_MOVES:
      return ReduxUtils.replace(state, 'dismissedMoves', action.dismissedMoves || {});

    default:
      return state;
  }
};
